import {css} from 'aphrodite';
import React from 'react';

import {getDefaultVisualizationConfigGQL} from 'analysis-tool/ui/charting/getDefaultVisualizationConfig';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {GET_SUBSET_DATA_FRAME_QUERY, DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import PresentationalUniversalBlockView from 'insights/universal-blocks/PresentationalUniversalBlockView';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {popoverPreviewStyles as styles} from './PopoverPreviewStyles';

/**
 * Popover contents for a data frame.
 */
export default function DataFramePopoverContents({
  rawData,
}: {
  rawData: PipelineStepRawData<PipelineStepDataKind.DATASET>;
}): JSX.Element {
  return (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId: rawData.id, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame}) => {
        assert(!isInaccessible(dataFrame), 'Expected data frame to be accessible.');
        return (
          /*
           * A fixed height is required so tables don't expand and are forced to use their internal scrollbar.
           * However, we only apply the fixed height for larger data frames, so there isn't whitespace in the
           * popover beneath small tables.
           */
          <div
            className={css(
              styles.dataFramePreview,
              dataFrame.numRows > 10 && styles.fixedHeightDataFramePreview
            )}
          >
            <PresentationalUniversalBlockView
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              inputDataFrame={dataFrame.dataFrameJson}
              visualizationConfig={getDefaultVisualizationConfigGQL(
                dataFrame.dataFrameJson.columns,
                VisualizationType.TABLE
              )}
              viewStateId={`analysis-flowchart-popover-${rawData.id}`}
              paneProps={{
                banner: (
                  <DataFrameWarningBanner
                    canDownloadCompleteData={false}
                    isRandomSubset={false}
                    name={dataFrame.dataFrameJson.name}
                    renderedColumns={dataFrame.dataFrameJson.columns.length}
                    renderedRows={dataFrame.dataFrameJson.rows.length}
                    totalColumns={dataFrame.allColumnsJson.length}
                    totalRows={dataFrame.numRows}
                  />
                ),
                title: dataFrame.dataFrameJson.name,
              }}
            />
          </div>
        );
      }}
    </BenchlingQuery>
  );
}
